import React, { useState, useEffect } from 'react';

// --- ICON LIBRARY ---
const ICON_LIBRARY = [
  { id: 'book',      icon: 'fa-book-open',      label: 'General' },
  { id: 'science',   icon: 'fa-microscope',     label: 'Science' },
  { id: 'atom',      icon: 'fa-atom',           label: 'Physics' },
  { id: 'flask',     icon: 'fa-flask',          label: 'Chem' },
  { id: 'math',      icon: 'fa-calculator',     label: 'Math' },
  { id: 'law',       icon: 'fa-scale-balanced', label: 'Law' },
  { id: 'code',      icon: 'fa-laptop-code',    label: 'Tech' },
  { id: 'health',    icon: 'fa-heart-pulse',    label: 'Health' },
  { id: 'history',   icon: 'fa-landmark',       label: 'History' },
  { id: 'art',       icon: 'fa-palette',        label: 'Arts' },
];

// --- COLOR OPTIONS ---
const COLOR_OPTIONS = [
  { id: 'blue',    bg: 'bg-blue-500',    ring: 'ring-blue-500' },
  { id: 'purple',  bg: 'bg-purple-500',  ring: 'ring-purple-500' },
  { id: 'emerald', bg: 'bg-emerald-500', ring: 'ring-emerald-500' },
  { id: 'orange',  bg: 'bg-orange-500',  ring: 'ring-orange-500' },
  { id: 'red',     bg: 'bg-red-500',     ring: 'ring-red-500' },
  { id: 'gray',    bg: 'bg-slate-500',   ring: 'ring-slate-500' },
];

const AddExamModal = ({ onClose, onSave, examToEdit }) => {
  const MAX_EXAM_NAME_LENGTH = 30;
  const [formData, setFormData] = useState({
    subjectCode: '',
    title: '',
    description: '',
    color: 'blue',
    icon: 'fa-book-open'
  });

  // ✅ Pre-fill the form if an exam is passed in to edit
  useEffect(() => {
    if (examToEdit) {
      setFormData({
        subjectCode: examToEdit.subjectCode || '',
        title: examToEdit.title || '',
        description: examToEdit.description || '',
        color: examToEdit.appearance?.color || examToEdit.color || 'blue',
        icon: examToEdit.appearance?.icon || examToEdit.icon || 'fa-book-open'
      });
    }
  }, [examToEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelect = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.subjectCode || !formData.title) return alert("Please fill in required fields");
    if (String(formData.title || "").trim().length > MAX_EXAM_NAME_LENGTH) {
      return alert(`Exam title must not exceed ${MAX_EXAM_NAME_LENGTH} characters.`);
    }

    if (onSave) {
        onSave({ ...formData, category: 'Mock Exam' });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden flex flex-col max-h-[90vh] border border-slate-100">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-white">
          <div className="flex items-center gap-3">
             <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600 border border-blue-100 shadow-sm">
               <i className={examToEdit ? "fa-solid fa-pen text-lg" : "fa-solid fa-layer-group text-lg"}></i>
             </div>
             <div>
               {/* ✅ Dynamic Header Text */}
               <h2 className="text-lg font-bold text-slate-800">
                 {examToEdit ? "Edit Exam Deck" : "New Exam Deck"}
               </h2>
               <p className="text-xs text-slate-500 font-medium">
                 {examToEdit ? "Update your workspace details" : "Create a workspace for your reviewer"}
               </p>
             </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors">
            <i className="fa-solid fa-xmark text-lg"></i>
          </button>
        </div>

        {/* Form Body */}
        <div className="overflow-y-auto custom-scrollbar">
          <form id="add-exam-form" onSubmit={handleSubmit} className="flex flex-col">
            
            {/* DETAILS SECTION */}
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                
                {/* Subject Code */}
                <div className="col-span-1 space-y-1.5">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wide ml-1">Code</label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <i className="fa-solid fa-hashtag text-slate-400 group-focus-within:text-blue-500 transition-colors"></i>
                    </div>
                    <input 
                      type="text" name="subjectCode" placeholder="BIO" maxLength={5} required
                      value={formData.subjectCode} onChange={handleChange}
                      className="block w-full pl-9 pr-3 py-2.5 text-sm font-bold uppercase text-slate-700 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all placeholder:text-slate-300 shadow-sm"
                    />
                  </div>
                </div>

                {/* Title */}
                <div className="col-span-1 sm:col-span-3 space-y-1.5">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wide ml-1">Title</label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <i className="fa-solid fa-pen text-slate-400 group-focus-within:text-blue-500 transition-colors text-xs"></i>
                    </div>
                    <input 
                      type="text" name="title" placeholder="Microbiology Finals" required maxLength={30}
                      value={formData.title} onChange={handleChange}
                      className="block w-full pl-9 pr-3 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all placeholder:text-slate-300 shadow-sm"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide ml-1">Description</label>
                <div className="relative group">
                    <div className="absolute top-3 left-3 flex items-start pointer-events-none">
                      <i className="fa-solid fa-align-left text-slate-400 group-focus-within:text-blue-500 transition-colors text-xs"></i>
                    </div>
                  <textarea 
                    name="description" rows="2" placeholder="What topics does this deck cover?"
                    value={formData.description} onChange={handleChange}
                    className="block w-full pl-9 pr-3 py-2.5 text-sm text-slate-600 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all placeholder:text-slate-300 resize-none shadow-sm"
                  ></textarea>
                </div>
              </div>
            </div>

            {/* APPEARANCE SECTION */}
            <div className="p-6 bg-slate-50/50 border-t border-slate-100 space-y-5">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Visual Style</h3>
              <div className="flex items-center gap-6">
                 <label className="text-xs font-bold text-slate-600 w-20">Card Color</label>
                 <div className="flex gap-3">
                  {COLOR_OPTIONS.map((opt) => (
                    <button key={opt.id} type="button" onClick={() => handleSelect('color', opt.id)}
                      className={`relative w-8 h-8 rounded-full ${opt.bg} transition-all duration-300 ${formData.color === opt.id ? `ring-2 ring-offset-2 ${opt.ring} scale-110 shadow-md` : 'hover:scale-110 hover:opacity-90 opacity-70'}`}
                    >
                      {formData.color === opt.id && <span className="absolute inset-0 flex items-center justify-center"><i className="fa-solid fa-check text-white text-[10px]"></i></span>}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 mb-3">Cover Icon</label>
                <div className="grid grid-cols-5 gap-3">
                  {ICON_LIBRARY.map((item) => (
                    <button key={item.id} type="button" onClick={() => handleSelect('icon', item.icon)}
                      className={`flex flex-col items-center justify-center p-3 rounded-xl border transition-all duration-200 gap-2 group relative overflow-hidden ${formData.icon === item.icon ? 'bg-white border-blue-500 text-blue-600 shadow-md ring-1 ring-blue-500' : 'bg-white border-slate-200 text-slate-400 hover:border-blue-300 hover:text-blue-500 hover:shadow-sm'}`}
                    >
                      <i className={`fa-solid ${item.icon} text-lg transition-transform group-hover:scale-110 duration-300`}></i>
                      <span className="text-[10px] font-bold">{item.label}</span>
                      {formData.icon === item.icon && <div className="absolute inset-0 bg-blue-50/10 pointer-events-none"></div>}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-slate-100 bg-white flex justify-end gap-3 z-10">
          <button onClick={onClose} className="px-5 py-2.5 rounded-xl text-sm font-bold text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors">Cancel</button>
          <button type="submit" form="add-exam-form" className="px-6 py-2.5 rounded-xl text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-600/30 transition-all hover:scale-[1.02] active:scale-95 flex items-center gap-2">
            {/* ✅ Dynamic Submit Button Text */}
            <i className={examToEdit ? "fa-solid fa-save text-xs" : "fa-solid fa-plus text-xs"}></i> 
            {examToEdit ? "Save Changes" : "Create Deck"}
          </button>
        </div>

      </div>
    </div>
  );
};

export default AddExamModal;
